<template>
    <Modal id="gift-aid-modal"
           icon="fa-hand-holding-dollar"
           title="GIFT_AID_MODAL.MODAL_TITLE">
        <template v-slot:default>
            <div v-if="askingOnCustomerScreen"
                 class="text-center">
                <PageLoadingSpinner :lang="displayedOnCustomerScreen ? 'GIFT_AID_MODAL.WAITING_ON_CUSTOMER' : null" />
                <SquareBtn v-if="displayedOnCustomerScreen"
                           color="default"
                           class="cancel-btn"
                           @click="answerHere">
                    {{ l('GIFT_AID_MODAL.ANSWER_HERE_BTN') }}
                </SquareBtn>
            </div>
            <div v-else
                 class="text-center">
                <img :src="'/epos/img/gift-aid-logo.png'"
                     alt="Gift Aid It"
                     class="gift-aid-logo" />
                <h4>
                    <strong>{{ l('GIFT_AID_MODAL.QUESTION_OPERATOR') }}</strong>
                </h4>
                <h4 v-if="additionalDonation">
                    {{ l('GIFT_AID_MODAL.DONATION_REQUIRED', { additionalDonation }) }}
                </h4>
                <h4>
                    {{ l('GIFT_AID_MODAL.OPERATOR_DECLARATION_INSTRUCTION') }}
                </h4>
                <div class="well">
                    <p>{{ l('GIFT_AID_MODAL.DECLARATION', { companyName: company.settings.charityName || company.name, totalDonation }) }}</p>
                </div>
            </div>
        </template>

        <template v-slot:footer>
            <div v-if="!askingOnCustomerScreen"
                 class="modl-footer">
                <SquareBtn size="lg"
                           color="danger"
                           class="cancel-btn"
                           @click="no">
                    {{ l('GIFT_AID_MODAL.NO_BTN') }}
                </SquareBtn>

                <SquareBtn v-if="additionalDonation"
                           size="lg"
                           color="default"
                           @click="priceOnly">
                    {{ l('GIFT_AID_MODAL.DONATE_WITHOUT_GIFT_AID_BTN') }}
                </SquareBtn>

                <SquareBtn size="lg"
                           color="success"
                           @click="yes">
                    {{ l('GIFT_AID_MODAL.YES_BTN') }}
                </SquareBtn>
            </div>
        </template>
    </Modal>
</template>

<script>
import CustomerScreenQuestion from '@/lib/CustomerScreen/CustomerScreenQuestion';
import CustomerScreenQuestionType from '@/lib/CustomerScreen/CustomerScreenQuestionType';
import Modal from '@/vue/components/Modals/Modal';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    components: {
        PageLoadingSpinner,
        SquareBtn,
        Modal,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        additionalDonation: {
            type: Number,
            default: null,
        },

        totalDonation: {
            type: Number,
            default: null,
        },
    },

    data() {
        return {
            askingOnCustomerScreen: false,
            displayedOnCustomerScreen: false,
            customerScreenQuestionID: null,
        };
    },

    computed: {
        company() {
            return this.$store.state.currentUser.company;
        },
    },

    created() {
        if (!container.currentDevice.device.customerTouchScreenEnabled) {
            this.askingOnCustomerScreen = false;
            return;
        }

        const askOnCustomerScreenPromise = this.askOnCustomerScreen();
        if (askOnCustomerScreenPromise) {
            this.askingOnCustomerScreen = true;
        }
        askOnCustomerScreenPromise
            .then((result) => {
                if (!this.askingOnCustomerScreen) {
                    // If we've already decided to answer here ignore any answers coming from the customer screen.
                    return;
                }
                this.resolve(result);
            })
            .catch((e) => {
                console.warn('Error asking Gift Aid question on customer screen:', e);
            })
            .finally(() => {
                this.askingOnCustomerScreen = false;
            });
    },

    methods: {
        /**
         * @return {Promise<{giftAid: boolean, giftAidPrices: boolean}|null>}
         */
        askOnCustomerScreen() {
            // Create a customer screen question for Gift Aid.
            const question = new CustomerScreenQuestion(
                CustomerScreenQuestionType.BOOLEAN,
                container.giftAidPrompter.getCustomerDeclarationHtml(this.additionalDonation, this.totalDonation)
            );

            this.customerScreenQuestionID = question.ID;
            this.displayedOnCustomerScreen = false;

            return container.customerScreenDataService
                .askQuestion(
                    question,
                    () => {},
                    () => {
                        this.displayedOnCustomerScreen = true;
                    }
                )
                .then((answer) => {
                    if (answer) {
                        return {
                            giftAid: true,
                            giftAidPrices: true,
                        };
                    }

                    return {
                        giftAid: false,
                        giftAidPrices: false,
                    };
                })
                .finally(() => {
                    this.displayedOnCustomerScreen = false;
                });
        },

        answerHere() {
            if (this.customerScreenQuestionID) {
                container.customerScreenDataService.cancelQuestion(this.customerScreenQuestionID, 'Operator cancelled');
            }
            this.askingOnCustomerScreen = false;
        },

        no() {
            this.resolve({
                giftAid: false,
                giftAidPrices: false,
            });
        },

        priceOnly() {
            this.resolve({
                giftAid: false,
                giftAidPrices: true,
            });
        },

        yes() {
            this.resolve({
                giftAid: true,
                giftAidPrices: true,
            });
        },
    },
};
</script>

<style lang="less">
#gift-aid-modal {
    .gift-aid-logo {
        margin: 0 auto 20px;
        width: 200px;
    }
}
</style>
